import { useState } from "react";
import type { Exercise, SpeechAttemptResult } from "../../types";
import { api } from "../../api/client";
import { useRecorder } from "../../hooks/useRecorder";
import { speakItalian, stopItalian } from "../../lib/tts";
import FeedbackPanel from "./FeedbackPanel";

interface Props {
  exercise: Exercise;
  onDone: (passed: boolean) => void;
  onSkip?: () => void;
}

function MicIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v4M8 23h8" />
    </svg>
  );
}

function SpeakerIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
      <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
    </svg>
  );
}

export default function SpeakingExercise({ exercise, onDone, onSkip }: Props) {
  const [isPlaying, setIsPlaying] = useState(false);
  const recorder = useRecorder();
  const [result, setResult] = useState<SpeechAttemptResult | null>(null);
  const [grading, setGrading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const isReadAloud = exercise.exercise_type === "read_aloud";
  const isRepeat = exercise.exercise_type === "repeat_aloud";
  // Both repeat_aloud and read_aloud show the target text and grade against it.
  const showsTarget = isRepeat || isReadAloud;
  // A "correct it aloud" task shows the WRONG sentence in `text` and grades against
  // the corrected `answer`. We detect it structurally: in plain repeat/phrase drills
  // the answer is a slice of the displayed sentence; in a correction it is not. For
  // those we relabel the prompt and hide Listen (which would read the wrong sentence).
  const norm = (s: string) =>
    s.toLowerCase().replace(/\s+/g, " ").replace(/[«».,;:!?…"']/g, "").trim();
  const isCorrection = isRepeat && !norm(exercise.text).includes(norm(exercise.answer));

  const stopAndGrade = async () => {
    const blob = await recorder.stop();
    if (!blob) {
      setUploadError("No audio was captured. Try again.");
      return;
    }
    setGrading(true);
    setUploadError(null);
    try {
      setResult(await api.attemptSpeech(exercise.id, blob));
    } catch (err) {
      console.error("Speech grading failed:", err);
      setUploadError("Couldn't grade the recording. Please try again.");
    } finally {
      setGrading(false);
    }
  };

  const retry = () => {
    setResult(null);
    setUploadError(null);
  };

  return (
    <div>
      {showsTarget ? (
        <>
          <p className="text-[0.7rem] uppercase tracking-[0.12em] text-text-tertiary font-semibold mb-1.5">
            {isReadAloud ? "Read this passage aloud" : isCorrection ? "Correct this aloud" : "Say this aloud"}
          </p>
          <div className="flex items-start gap-3 mb-5">
            <p className={`leading-relaxed font-display text-ink flex-1 ${isReadAloud ? "text-[1.05rem] whitespace-pre-line" : "text-[1.15rem]"}`}>{exercise.text}</p>
            {!isCorrection && (
              <button
                onClick={() => {
                  if (isPlaying) {
                    stopItalian();
                    setIsPlaying(false);
                  } else {
                    speakItalian(exercise.text, () => setIsPlaying(false));
                    setIsPlaying(true);
                  }
                }}
                aria-label={isPlaying ? "Stop" : "Listen"}
                className="btn-quiet text-xs px-3 py-2 flex-shrink-0"
              >
                <SpeakerIcon /> {isPlaying ? "Stop" : "Listen"}
              </button>
            )}
          </div>
        </>
      ) : (
        <p className="text-[1.05rem] leading-relaxed font-display text-ink mb-5">{exercise.text}</p>
      )}

      {!result && (
        <div className="flex flex-col items-center gap-3 py-4">
          {!recorder.recording ? (
            <button
              onClick={recorder.start}
              disabled={grading}
              className="btn-primary text-sm px-6 py-3 rounded-full"
            >
              <MicIcon /> {grading ? "Grading…" : "Record"}
            </button>
          ) : (
            <button
              onClick={stopAndGrade}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-error text-cream text-sm font-medium animate-pulse-soft"
            >
              <span className="w-2.5 h-2.5 rounded-full bg-cream" /> Stop · {recorder.seconds}s
            </button>
          )}
          {recorder.error && <p className="text-error text-xs">{recorder.error}</p>}
          {uploadError && <p className="text-error text-xs">{uploadError}</p>}
          {!recorder.recording && !recorder.error && !uploadError && (
            <p className="text-text-tertiary text-[0.7rem]">Tap to record · up to 60s</p>
          )}
          {!recorder.recording && onSkip && (
            <button onClick={onSkip} className="btn-quiet text-xs px-3 py-1.5">Skip</button>
          )}
        </div>
      )}

      {result && (
        <FeedbackPanel
          correct={result.passed}
          title={result.passed ? `Passed · ${Math.round(result.score)}/100` : `Keep practicing · ${Math.round(result.score)}/100`}
          onNext={() => onDone(result.passed)}
          onRetry={retry}
        >
          {result.transcript && (
            <div>
              Heard: <span className="text-text-secondary italic">"{result.transcript}"</span>
            </div>
          )}
          {result.feedback && <div className="italic">{result.feedback}</div>}
          {result.mispronounced_words.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {result.mispronounced_words.map((w) => (
                <span key={w} className="text-[0.7rem] px-2 py-0.5 rounded-full bg-error/15 text-error">
                  {w}
                </span>
              ))}
            </div>
          )}
        </FeedbackPanel>
      )}
    </div>
  );
}
